/**
 * Browser-bound delegated session enrollment for human workflow submits.
 * Login must use credentials:include so the HttpOnly binding cookie sticks;
 * apiClient uses credentials:omit for every other call.
 */

import { getRequestContext } from "~/api/requestContextStore";
import { apiConfig } from "~/config/api";
import { getAccessToken } from "~/features/auth/services/keycloakService";
import { isSafeRelativePath, safeSameOriginPath } from "~/lib/safeUrl";

const PENDING_KEY = "workflow.pendingSubmit";
/** Sanitised return target, stored separately so CodeQL does not taint it with workflow `input`. */
const PENDING_RETURN_PATH_KEY = "workflow.pendingReturnPath";
const HANDLE_KEY = "workflow.sessionBindingHandle";
const HANDLE_EXPIRES_KEY = "workflow.sessionBindingExpiresAt";

/** Where a CREATE submit should land after the Keycloak session round-trip. */
export type PendingCreateResume = {
  intent: "stay" | "new" | "go";
  resource: string;
  version: string;
  recordPageKey?: string;
};

export type PendingWorkflowSubmit = {
  definitionKey: string;
  input: Record<string, unknown>;
  idempotencyKey: string;
  definitionVersion?: string;
  context: unknown;
  returnPath: string;
  tenantId?: string;
  /** Set only for a CREATE nav submit. Absent submits still open the run page. */
  createResume?: PendingCreateResume;
};

export const storePendingWorkflowSubmit = (pending: PendingWorkflowSubmit): void => {
  sessionStorage.setItem(PENDING_RETURN_PATH_KEY, safeSameOriginPath(pending.returnPath));
  const { returnPath: _ignored, ...rest } = pending;
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(rest));
};

/** Same-origin path saved for the pending submit. Not read from the pending JSON blob. */
export const peekEnrollmentReturnPath = (): string =>
  safeSameOriginPath(sessionStorage.getItem(PENDING_RETURN_PATH_KEY) ?? "/");

export const peekPendingWorkflowSubmit = (): PendingWorkflowSubmit | null => {
  const raw = sessionStorage.getItem(PENDING_KEY);
  if (!raw) {
    return null;
  }
  try {
    const stored = JSON.parse(raw) as Partial<PendingWorkflowSubmit>;
    const legacyReturn = typeof stored.returnPath === "string" ? stored.returnPath : undefined;
    const { returnPath: _legacy, ...rest } = stored;
    return {
      ...(rest as Omit<PendingWorkflowSubmit, "returnPath">),
      returnPath: legacyReturn ? safeSameOriginPath(legacyReturn) : peekEnrollmentReturnPath(),
    };
  } catch {
    return null;
  }
};

export const clearPendingWorkflowSubmit = (): void => {
  sessionStorage.removeItem(PENDING_KEY);
  sessionStorage.removeItem(PENDING_RETURN_PATH_KEY);
};

export const storeSessionBindingHandle = (handle: string, expiresAt?: string): void => {
  sessionStorage.setItem(HANDLE_KEY, handle);
  if (expiresAt) {
    sessionStorage.setItem(HANDLE_EXPIRES_KEY, expiresAt);
  }
};

export const takeSessionBindingHandle = (): string | null => {
  const handle = sessionStorage.getItem(HANDLE_KEY);
  if (!handle) {
    return null;
  }
  const expiresAt = sessionStorage.getItem(HANDLE_EXPIRES_KEY);
  sessionStorage.removeItem(HANDLE_KEY);
  sessionStorage.removeItem(HANDLE_EXPIRES_KEY);
  if (expiresAt) {
    const expiry = Date.parse(expiresAt);
    if (!Number.isNaN(expiry) && Date.now() >= expiry) {
      return null;
    }
  }
  return handle;
};

export const peekSessionBindingHandle = (): string | null => {
  const handle = sessionStorage.getItem(HANDLE_KEY);
  if (!handle) {
    return null;
  }
  const expiresAt = sessionStorage.getItem(HANDLE_EXPIRES_KEY);
  if (expiresAt) {
    const expiry = Date.parse(expiresAt);
    if (!Number.isNaN(expiry) && Date.now() >= expiry) {
      return null;
    }
  }
  return handle;
};

const SESSIONS_PATH = "/workflow-service/v1/execution-identity/sessions";
const ENROLLMENT_RETURN_KEY = "workflow.enrollmentReturnPath";
const PREWARMED_KEY = "workflow.sessionPrewarmed";

const enrollmentHeaders = async (): Promise<Record<string, string>> => {
  const ctx = getRequestContext();
  const tenantId = ctx?.tenantId?.trim();
  if (!tenantId) {
    throw new Error("Tenant context is required for workflow session enrollment");
  }

  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error("Authenticated session is required for workflow session enrollment");
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
    "x-tenant-id": tenantId,
    "x-security-context": ctx?.securityContext || tenantId,
  };
  if (apiConfig.apiKey) {
    headers["x-api-key"] = apiConfig.apiKey;
  }
  if (apiConfig.serviceName) {
    headers["x-cpes-service-name"] = apiConfig.serviceName;
  }
  return headers;
};

const unwrapData = (body: Record<string, unknown> | null): Record<string, unknown> | null =>
  body && typeof body.data === "object" && body.data !== null
    ? (body.data as Record<string, unknown>)
    : body;

export type SilentSessionHandle =
  | { kind: "issued"; handle: string; expiresAt?: string }
  /** The browser has no reusable workflow session; only a provider login can create one. */
  | { kind: "enrollment-required" }
  /** Network, outage or an older service without the endpoint; do not redirect on this. */
  | { kind: "unavailable" };

/**
 * Asks the workflow service for a fresh one-use handle from this browser's existing
 * session (HttpOnly cookie set by the enrollment callback). Never navigates.
 */
export const requestSilentSessionHandle = async (): Promise<SilentSessionHandle> => {
  try {
    const res = await fetch(`${apiConfig.workflowServiceBaseUrl}${SESSIONS_PATH}/handle`, {
      method: "POST",
      credentials: "include",
      headers: await enrollmentHeaders(),
      body: JSON.stringify({ evidenceProfile: "DELEGATED_EXCHANGE" }),
    });
    if (res.status === 400) {
      return { kind: "enrollment-required" };
    }
    if (!res.ok) {
      return { kind: "unavailable" };
    }
    const data = unwrapData((await res.json().catch(() => null)) as Record<string, unknown> | null);
    const handle =
      data && typeof data.sessionBindingHandle === "string" ? data.sessionBindingHandle : null;
    if (!handle) {
      return { kind: "unavailable" };
    }
    const expiresAt = typeof data?.expiresAt === "string" ? data.expiresAt : undefined;
    return { kind: "issued", handle, expiresAt };
  } catch {
    return { kind: "unavailable" };
  }
};

/** Same-origin channel the sign-in popup uses to hand its handle back to the tab that opened it. */
export const ENROLLMENT_CHANNEL = "workflow-session-enrollment";

export type EnrollmentMessage =
  | { type: "handle"; handle: string; expiresAt?: string }
  | { type: "taken" };

const POPUP_NAME = "workflow-session-enrollment";
const POPUP_FEATURES = "popup,width=520,height=720";
const POPUP_TIMEOUT_MS = 5 * 60_000;
/** A closed popup may still be posting its handle; wait this long before giving up. */
const POPUP_CLOSED_GRACE_MS = 1_500;

/** Starts DELEGATED_EXCHANGE enrollment and returns the Keycloak URL to open. */
const requestAuthorizationUrl = async (): Promise<string> => {
  const ctx = getRequestContext();
  const tenantId = ctx?.tenantId?.trim();
  if (!tenantId) {
    throw new Error("Tenant context is required for workflow session enrollment");
  }

  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error("Authenticated session is required for workflow session enrollment");
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
    "x-tenant-id": tenantId,
    "x-security-context": ctx?.securityContext || tenantId,
  };
  if (apiConfig.apiKey) {
    headers["x-api-key"] = apiConfig.apiKey;
  }
  if (apiConfig.serviceName) {
    headers["x-cpes-service-name"] = apiConfig.serviceName;
  }

  const base = apiConfig.workflowServiceBaseUrl;
  const res = await fetch(`${base}/workflow-service/v1/execution-identity/sessions/login`, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify({
      evidenceProfile: "DELEGATED_EXCHANGE",
      browserReturnUrl: `${window.location.origin}/workflow-session-return`,
    }),
  });

  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!res.ok) {
    const nested =
      body && typeof body.data === "object" && body.data !== null
        ? (body.data as Record<string, unknown>)
        : body;
    const message =
      (nested && typeof nested.detail === "string" && nested.detail) ||
      (nested && typeof nested.title === "string" && nested.title) ||
      (body && typeof body.message === "string" && body.message) ||
      `Session login failed (HTTP ${res.status})`;
    throw new Error(message);
  }

  const data =
    body && typeof body.data === "object" && body.data !== null
      ? (body.data as Record<string, unknown>)
      : body;
  const authorizationUrl =
    data && typeof data.authorizationUrl === "string" ? data.authorizationUrl : null;
  if (!authorizationUrl) {
    throw new Error("Session login response omitted authorizationUrl");
  }

  return authorizationUrl;
};

/**
 * Starts DELEGATED_EXCHANGE enrollment and navigates to Keycloak.
 * Does not settle after a successful redirect.
 */
export const beginDelegatedSessionEnrollment = async (): Promise<never> => {
  window.location.assign(await requestAuthorizationUrl());
  return new Promise(() => undefined);
};

export const takeEnrollmentReturnPath = (): string | null => {
  const path = sessionStorage.getItem(ENROLLMENT_RETURN_KEY);
  sessionStorage.removeItem(ENROLLMENT_RETURN_KEY);
  if (!path || !isSafeRelativePath(path)) {
    return null;
  }
  return safeSameOriginPath(path);
};

/**
 * Runs once per tab after sign-in so the provider trip happens while the user is
 * still arriving, not when they start a workflow. Returns `true` when this browser
 * already holds a reusable session, or a handle is now stored for the next submit.
 */
export const prewarmWorkflowSession = async (): Promise<boolean> => {
  if (sessionStorage.getItem(PREWARMED_KEY)) {
    return true;
  }
  sessionStorage.setItem(PREWARMED_KEY, "1");
  const silent = await requestSilentSessionHandle();
  if (silent.kind === "issued") {
    storeSessionBindingHandle(silent.handle, silent.expiresAt);
    return true;
  }
  if (silent.kind === "unavailable") {
    return false;
  }
  sessionStorage.setItem(
    ENROLLMENT_RETURN_KEY,
    safeSameOriginPath(
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    ),
  );
  await beginDelegatedSessionEnrollment();
  return false;
};

const isHandleMessage = (value: unknown): value is Extract<EnrollmentMessage, { type: "handle" }> =>
  typeof value === "object" &&
  value !== null &&
  (value as { type?: unknown }).type === "handle" &&
  typeof (value as { handle?: unknown }).handle === "string";

/** Resolves with the handle the popup's return page broadcasts. */
const waitForPopupHandle = (popup: Window): Promise<string> =>
  new Promise((resolve, reject) => {
    const channel = new BroadcastChannel(ENROLLMENT_CHANNEL);
    let closedAt: number | undefined;

    const finish = () => {
      clearInterval(poll);
      clearTimeout(timer);
      channel.close();
    };

    channel.onmessage = (event: MessageEvent<unknown>) => {
      if (!isHandleMessage(event.data)) {
        return;
      }

      channel.postMessage({ type: "taken" } satisfies EnrollmentMessage);
      finish();
      resolve(event.data.handle);
    };

    const poll = setInterval(() => {
      if (!popup.closed) {
        return;
      }

      closedAt ??= Date.now();

      if (Date.now() - closedAt >= POPUP_CLOSED_GRACE_MS) {
        finish();
        reject(new Error("The sign-in window was closed before it finished."));
      }
    }, 500);

    const timer = setTimeout(() => {
      finish();
      popup.close();
      reject(new Error("Workflow sign-in timed out."));
    }, POPUP_TIMEOUT_MS);
  });

/**
 * Opens the sign-in popup inside the click's user activation, before any
 * network wait, so the browser does not block it. `null` means blocked.
 */
const openEnrollmentPopup = (): Window | null => {
  if (typeof BroadcastChannel === "undefined") {
    return null;
  }

  return window.open("about:blank", POPUP_NAME, POPUP_FEATURES);
};

/**
 * Returns a live session binding handle.
 *
 * Without one, Keycloak opens in a popup and the submit carries on in this
 * tab once the popup hands the handle back, so the form, the socket and the
 * page stay where they are. A blocked popup falls back to the full-page
 * redirect, which resumes the submit from `/workflow-session-return`.
 */
export const resolveSessionBindingForSubmit = async (
  pending: PendingWorkflowSubmit,
): Promise<string> => {
  const existing = peekSessionBindingHandle();
  if (existing) {
    return takeSessionBindingHandle() ?? existing;
  }

  const silent = await requestSilentSessionHandle();
  if (silent.kind === "issued") {
    return silent.handle;
  }

  const popup = openEnrollmentPopup();

  if (!popup) {
    storePendingWorkflowSubmit(pending);
    await beginDelegatedSessionEnrollment();
    throw new Error("Enrollment redirect did not navigate");
  }

  try {
    popup.location.href = await requestAuthorizationUrl();
    return await waitForPopupHandle(popup);
  } catch (error) {
    popup.close();
    throw error;
  }
};
